import { Link } from "wouter";
import { CheckCircle, Circle, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { User as UserType } from "@shared/schema";

interface OnboardingStep {
  id: string;
  label: string;
  href: string;
  isComplete: boolean;
}

interface OnboardingProgressProps {
  user: UserType;
  className?: string;
}

function getFounderOnboardingSteps(user: UserType): OnboardingStep[] {
  return [
    {
      id: "basic-profile",
      label: "Complete basic profile",
      href: "/profile?edit=intro",
      isComplete: !!(user.firstName && user.lastName && user.email),
    },
    {
      id: "company-info",
      label: "Add company information",
      href: "/register/founder",
      isComplete: !!(user.founderCompanyName && user.founderStage),
    },
    {
      id: "one-liner",
      label: "Write your one-liner pitch",
      href: "/founder/one-page-brief",
      isComplete: !!user.founderOneLiner,
    },
    {
      id: "deck-link",
      label: "Add your pitch deck",
      href: "/register/founder",
      isComplete: !!user.founderDeckLink,
    },
    {
      id: "sector-tags",
      label: "Add sector tags",
      href: "/register/founder",
      isComplete: !!(user.founderSectorTags && user.founderSectorTags.length > 0),
    },
  ];
}

function getAdvisorOnboardingSteps(user: UserType): OnboardingStep[] {
  return [
    {
      id: "basic-profile",
      label: "Complete basic profile",
      href: "/profile?edit=intro",
      isComplete: !!(user.firstName && user.lastName && user.email),
    },
    {
      id: "headline",
      label: "Add your headline",
      href: "/profile?edit=intro",
      isComplete: !!user.headline,
    },
    {
      id: "expertise",
      label: "List your expertise",
      href: "/profile?edit=expertise",
      isComplete: !!(user.expertise && user.expertise.length > 0),
    },
    {
      id: "bio",
      label: "Write your bio",
      href: "/profile?edit=intro",
      isComplete: !!user.bio,
    },
    {
      id: "linkedin",
      label: "Connect LinkedIn",
      href: "/profile?edit=links",
      isComplete: !!user.linkedinUrl,
    },
  ];
}

function getInvestorOnboardingSteps(user: UserType): OnboardingStep[] {
  return [
    {
      id: "basic-profile",
      label: "Complete basic profile",
      href: "/profile?edit=intro",
      isComplete: !!(user.firstName && user.lastName && user.email),
    },
    {
      id: "company",
      label: "Add your firm/company",
      href: "/capital/investor-apply",
      isComplete: !!user.company,
    },
    {
      id: "sectors",
      label: "Select investment sectors",
      href: "/capital/investor-apply",
      isComplete: !!(user.sectors && user.sectors.length > 0),
    },
    {
      id: "check-size",
      label: "Specify your check size",
      href: "/capital/investor-apply",
      isComplete: !!user.checkSize,
    },
    {
      id: "investment-stage",
      label: "Set preferred stage",
      href: "/capital/investor-apply",
      isComplete: !!user.investmentStage,
    },
  ];
}

function getExecutiveOnboardingSteps(user: UserType): OnboardingStep[] {
  return [
    {
      id: "basic-profile",
      label: "Complete basic profile",
      href: "/profile?edit=intro",
      isComplete: !!(user.firstName && user.lastName && user.email),
    },
    {
      id: "headline",
      label: "Add your headline",
      href: "/profile?edit=intro",
      isComplete: !!user.headline,
    },
    {
      id: "company-title",
      label: "Add your current role",
      href: "/profile?edit=intro",
      isComplete: !!(user.company && user.title),
    },
    {
      id: "bio",
      label: "Write your bio",
      href: "/profile?edit=intro",
      isComplete: !!user.bio,
    },
    {
      id: "expertise",
      label: "List your expertise",
      href: "/profile?edit=expertise",
      isComplete: !!(user.expertise && user.expertise.length > 0),
    },
  ];
}

function getDefaultOnboardingSteps(user: UserType): OnboardingStep[] {
  return [
    {
      id: "basic-profile",
      label: "Complete basic profile",
      href: "/profile?edit=intro",
      isComplete: !!(user.firstName && user.lastName && user.email),
    },
    {
      id: "headline",
      label: "Add your headline",
      href: "/profile?edit=intro",
      isComplete: !!user.headline,
    },
    {
      id: "bio",
      label: "Write your bio",
      href: "/profile?edit=intro",
      isComplete: !!user.bio,
    },
    {
      id: "profile-image",
      label: "Add a profile photo",
      href: "/profile?edit=intro",
      isComplete: !!user.profileImageUrl,
    },
  ];
}

function getOnboardingStepsForRole(user: UserType): OnboardingStep[] {
  switch (user.role) {
    case "founder":
      return getFounderOnboardingSteps(user);
    case "advisor":
      return getAdvisorOnboardingSteps(user);
    case "investor":
      return getInvestorOnboardingSteps(user);
    case "executive":
    case "board_candidate":
      return getExecutiveOnboardingSteps(user);
    default:
      return getDefaultOnboardingSteps(user);
  }
}

export function OnboardingProgress({ user, className }: OnboardingProgressProps) {
  const steps = getOnboardingStepsForRole(user);
  const completedCount = steps.filter((step) => step.isComplete).length;
  const totalCount = steps.length;
  const progressPercent = Math.round((completedCount / totalCount) * 100);

  const nextIncompleteStep = steps.find((step) => !step.isComplete);

  if (progressPercent === 100) {
    return null;
  }

  return (
    <Card className={cn("", className)} data-testid="card-onboarding-progress">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <CardTitle className="text-lg font-semibold" data-testid="text-onboarding-title">
            Profile Completion
          </CardTitle>
          <span className="text-sm font-medium text-muted-foreground" data-testid="text-onboarding-percent">
            {progressPercent}% complete
          </span>
        </div>
        <Progress value={progressPercent} className="h-2 mt-2" data-testid="progress-onboarding" />
      </CardHeader>
      <CardContent className="space-y-3">
        {steps.map((step) => (
          <div
            key={step.id}
            className={cn(
              "flex items-center gap-3 py-2",
              step.isComplete && "opacity-60"
            )}
            data-testid={`step-${step.id}`}
          >
            {step.isComplete ? (
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
            ) : (
              <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            )}
            <span
              className={cn(
                "flex-1 text-sm",
                step.isComplete ? "text-muted-foreground line-through" : "text-foreground"
              )}
              data-testid={`text-step-label-${step.id}`}
            >
              {step.label}
            </span>
            {!step.isComplete && (
              <Link href={step.href}>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-primary"
                  data-testid={`button-complete-${step.id}`}
                >
                  Complete
                  <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </Link>
            )}
          </div>
        ))}

        {nextIncompleteStep && (
          <div className="pt-3 border-t border-border">
            <Button asChild className="w-full" data-testid="button-continue-profile">
              <Link href={nextIncompleteStep.href}>
                Continue Setting Up Profile
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
